import React, { useState, useEffect } from "react";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import Head from "next/head";
import { ThreeDots } from "react-loader-spinner";
import { ENDPOINTS, WEB_URL } from "@/config/endpoints";
import esMX from "@/locales/blogs/esMX";
import enIN from "@/locales/blogs/enIN";
import enUS from "@/locales/blogs/enUS";
import IMAGES from "@/public/assets/img";
import { generateAuthorPageSchema } from "@/component/shared/blog/authorPageSchema";
import BreadCrumb from "@/component/shared/breadCrumb";
import { blogBreadcrumbSchema } from "@/lib/blogBreadcrumSchema";

const Pagination = dynamic(() => import("@/component/shared/pagination"), {
  loading: () => <p>Loading...</p>,
});

const index = ({
  authorDetails,
  initialPosts,
  totalPagesCount,
  currentPageFromServer,
  recentPosts,
}) => {
  const [posts, setPosts] = useState(initialPosts);
  const [currentPage, setCurrentPage] = useState(currentPageFromServer);
  const [totalPages, setTotalPages] = useState(totalPagesCount);
  const [loader, setLoader] = useState(false);
  const linkedInUrl = authorDetails.yoast_head_json.schema[
    "@graph"
  ][3].sameAs.find((url) => url.includes("linkedin.com"));
  const authorProfileImage =
    authorDetails.yoast_head_json.schema["@graph"][3].sameAs[1] || "";
  const authorDesignation =
    authorDetails.yoast_head_json.schema["@graph"][3].sameAs[2] || "";

  const router = useRouter();
  const { locale } = router;
  const t =
    (locale === "es-mx" && esMX) ||
    (locale === "en-us" && enUS) ||
    (locale === "en-in" && enIN);

  const metaData = authorDetails.yoast_head_json;
  const imagePath = IMAGES.HOMEIMG.bigLogoNew.src;
  const pageUrl = `${WEB_URL}${locale === "es-mx" ? "/es-mx/author/" : "/author/"
    }${authorDetails.slug}`;

  useEffect(() => {
    setPosts(initialPosts);
    setTotalPages(totalPagesCount);
    setCurrentPage(currentPageFromServer);
  }, [initialPosts, totalPagesCount, currentPageFromServer]);

  useEffect(() => {
    const handleRouteChangeComplete = () => setLoader(false);
    router.events.on("routeChangeComplete", handleRouteChangeComplete);
    router.events.on("routeChangeError", handleRouteChangeComplete);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
      router.events.off("routeChangeError", handleRouteChangeComplete);
    };
  }, [router]);

  const handlePageClick = (page) => {
    setLoader(true);
    router.push({
      pathname: `/author/${authorDetails.slug}`,
      query: { page },
    });
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageClick(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      handlePageClick(currentPage - 1);
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageDisplay = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxPageDisplay / 2));
    let endPage = startPage + maxPageDisplay - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxPageDisplay + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => handlePageClick(i)}
          className={currentPage === i ? "active" : ""}
        >
          {i}
        </button>
      );
    }

    return pageNumbers;
  };

  if (!posts || posts.length === 0) {
    return (
      <div className="fixed inset-0 z-[9999] items-center flex bg-[rgba(255,255,255,0.8)]">
        <div className="w-[100px] mx-auto h-[100px] justify-center flex">
          <ThreeDots
            height="80"
            width="80"
            radius="9"
            color="#032B5C"
            ariaLabel="three-dots-loading"
            wrapperStyle={{}}
            wrapperClassName="m-auto"
            visible={true}
          />
        </div>
      </div>
    );
  }

  const dateFormat = (publishDate) => {
    const dateString = new Date(publishDate);
    const options = { day: "numeric", year: "numeric", month: "long" };
    if (
      typeof dateString === "object" &&
      dateString !== null &&
      "toLocaleDateString" in dateString
    ) {
      const result = dateString.toLocaleDateString("en-US", options);
      return <span>{result}</span>;
    }
  };
  const dateFormatSpanish = (publishDate) => {
    const dateString = new Date(publishDate);
    const options = { day: "numeric", year: "numeric", month: "long" };
    if (
      typeof dateString === "object" &&
      dateString !== null &&
      "toLocaleDateString" in dateString
    ) {
      const result = dateString.toLocaleDateString("es-MX", options);
      return <span>{result}</span>;
    }
  };

  const truncateString = (str, maxLength) => {
    if (str.length <= maxLength) {
      return str; // No truncation needed
    }
    const truncatedString = str.slice(0, maxLength - 3) + "...";
    return truncatedString;
  };
  const truncatedTitle = (title) => {
    const originalString = title;
    const maxLength = 50;
    return truncateString(originalString, maxLength);
  };

  const toTitleCase = (str) => {
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };
  const authorPageSchema = generateAuthorPageSchema(
    authorDetails,
    recentPosts,
    locale
  );
  const breadcrumbSchema = blogBreadcrumbSchema({
    homeUrl: WEB_URL + `${locale == 'es-mx' ? '/es-mx' : ''}`,
    blogTitle: t.authorData.breadCreumbPageTitle,
    locale
  });
  return (
    <main className={locale === "es-mx" ? "sm:mt-24 mt-28" : "mt-[150px]"}>
      <Head>
        <title>{metaData.title}</title>
        <meta name="description" content={metaData.description} />
        <link rel="icon" href={imagePath} />
        <meta property="og:title" content={metaData.title} />
        <meta property="og:description" content={metaData.description} />
        <meta property="og:image" content={imagePath} />
        <meta property="og:url" content={pageUrl} />
        <link rel="canonical" href={pageUrl} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(authorPageSchema),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbSchema)
          }}
        />
      </Head>
      <section className="container px-3 pb-20">
        <BreadCrumb parentPage={'Blog'} parentSlug={'blogs'} page={t.authorData.breadCreumbPageTitle} />
        <div className="bg-[#FFFFE0] border border-[#E6E6E6] p-2 rounded-xl mt-6">
          <div className="flex gap-4 sm:flex-col items-center">
            <Image
              src={authorProfileImage}
              width={190}
              height={200}
              alt="author image"
              className="h-[200px]"
            />
            <div>
              <h2 className="text-lg font-medium sm:text-base">
                {authorDetails.name}
              </h2>
              <p className="text-base2 font-medium text-black mt-xsp sm:text-sm flex items-center">
                {authorDesignation}{" "}
                <Link
                  href={linkedInUrl}
                  className="mx-1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Image
                    className="w-4 h-4"
                    src={IMAGES.FOOTER.linkedIn}
                    alt="linkedin"
                  />
                </Link>
              </p>
              <p className="text-base text-baseBlack mt-xsp sm:text-sm">
                <span className="font-medium text-black">
                  {t.authorData.authorBioTitle}:
                </span>{" "}
                {authorDetails.description}
              </p>
            </div>
          </div>
        </div>

        <div className="w-full mt-6">
          <h1 className="text-xxl sm:text-lg text-secondary font-medium pb-3 text-center">{t.authorData.authorH2}</h1>
          <ul className="flex flex-wrap flex-1 -mx-xsp">
            {posts.map((post, i) => (
              <li
                key={post.id}
                className="md:w-1/2 sm:w-full py-2 px-1 list-none w-1/4"
              >
                <div className="bg-white shadow-cardShadow2 border border-[#E6E6E6] min-h-[300px] hover:shadow-cardHover2 transition-shadow animate-fade-in">
                  <Link
                    href={
                      WEB_URL +
                      `${locale == "es-mx" ? "/es-mx/blogs/" : "/blogs/"}${post.slug
                      }`
                    }
                  >
                    {post._embedded && post._embedded["wp:featuredmedia"] && (
                      <Image
                        className="w-full min-h-[165px] sm:h-[209px] object-fill"
                        priority={i === 0 ? true : false}
                        src={post._embedded["wp:featuredmedia"][0].source_url}
                        fetchPriority={i === 0 ? "high" : "auto"}
                        alt={post.title.rendered}
                        width={640}
                        height={427}
                      />
                    )}
                  </Link>

                  <div className="px-2 pt-3 sm:min-h-[150px]">
                    <Link
                      href={
                        WEB_URL +
                        `${locale == "es-mx" ? "/es-mx/blogs/" : "/blogs/"}${post.slug
                        }`
                      }
                    >
                      <p className="text-xs text-[#7b7b7b]">
                        {locale == "en-in" && dateFormat(post.date)}
                        {locale == "es-mx" && dateFormatSpanish(post.date)}
                      </p>
                      <h3
                        className="text-sm text-secondary my-1 mb-3 min-h-[40px] break-words"
                        dangerouslySetInnerHTML={{
                          __html: truncatedTitle(post.title.rendered),
                        }}
                      />
                      <div className="flex items-end justify-between mb-1">
                        <button className="flex item-center text-brand text-xs">
                          {locale == "en-in" ? "Read More" : "Leer más"}
                          <span className="ml-1">
                            <svg
                              xmlns="https://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              width="18"
                              height="18"
                            >
                              <path
                                d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"
                                fill="rgba(2,43,92,1)"
                              ></path>
                            </svg>
                          </span>
                        </button>
                      </div>
                    </Link>
                    {locale == "en-in" &&
                      post._embedded &&
                      post._embedded["wp:term"] && (
                        <div className="mb-3">
                          {post._embedded["wp:term"][0].map(
                            (category) =>
                              category.name != "Blog" &&
                              category.name != "Spanish Blog" && (
                                <Link
                                  key={category.id}
                                  className="text-[12px] text-brand bg-[#ddd] rounded-md py-[3px] px-[5px] mr-1 hover:underline"
                                  href={
                                    WEB_URL +
                                    `/blogs/category/${category.id}/${category.slug}`
                                  }
                                >
                                  {toTitleCase(category.name)}
                                </Link>
                              )
                          )}
                        </div>
                      )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>
      {!loader && (
        <Pagination
          pageNumbers={renderPageNumbers()}
          handlePrevPage={handlePrevPage}
          handleNextPage={handleNextPage}
          currentPage={currentPage}
          totalPages={totalPages}
        />
      )}

      {loader && (
        <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-80">
          <ThreeDots color="#032B5C" height="80" width="80" />
        </div>
      )}
    </main>
  );
};

export default index;

export async function getServerSideProps(context) {
  const slug = context.query.author;
  const { locale, query } = context;
  const page = query.page ? parseInt(query.page) : 1;

  try {
    // 1 Fetch Author
    const authorApi = await fetch(
      `${ENDPOINTS.WORDPRESS_AUTHOR_DETAIL_API}?search=${slug}`
    );
    const authorResponse = await authorApi.json();
    const author = authorResponse.find((item) => item.slug === slug);

    if (!author) {
      return { notFound: true };
    }

    // 2 Fetch Author Posts
    const url =
      locale === "es-mx"
        ? `${ENDPOINTS.WORDPRESS_MAIN_API}?author=${author.id}&_embed&lang=es&page=${page}&per_page=12`
        : `${ENDPOINTS.WORDPRESS_MAIN_API}?author=${author.id}&_embed&page=${page}&per_page=12`;

    const authorRelatedBlogsApi = await fetch(url);

    const initialPostsRes = await authorRelatedBlogsApi.json();
    const initialPosts = initialPostsRes.map((post) => ({
      title: post.title,
      date: post.date,
      slug: post.slug,
      _embedded: post._embedded,
      id: post.id,
    }));

    // 3 Fetch Recent Posts
    const recentAPIUrl =
      locale === "es-mx"
        ? `${ENDPOINTS.WORDPRESS_MAIN_API}?per_page=1&orderby=date&order=desc&author=${author.id}&_embed&lang=es`
        : `${ENDPOINTS.WORDPRESS_MAIN_API}?per_page=1&orderby=date&order=desc&author=${author.id}&_embed`;

    const recentBlogs = await fetch(recentAPIUrl);
    const recentBlogsData = await recentBlogs.json();
    const recentPosts = recentBlogsData.map((post) => ({
      title: post.title,
      date: post.date,
      slug: post.slug,
      _embedded: post._embedded,
      id: post.id,
    }));

    const totalPagesCount =
      parseInt(authorRelatedBlogsApi.headers.get("x-wp-totalpages")) || 1;

    return {
      props: {
        authorDetails: author,
        initialPosts,
        totalPagesCount,
        currentPageFromServer: page,
        recentPosts: recentPosts.length > 0 ? recentPosts[0] : [],
      },
    };
  } catch (error) {
    console.error("SSR Error:", error);
    return { notFound: true };
  }
}
